import Phaser from 'phaser';
import { STAGES, type StageConfig } from '../sim/course';
import { formatTime } from '../sim/score';
import { Records, boardKey, todayKey, type RunRecord } from '../records';
import { lang, setLang, stageName, t, type Lang } from '../i18n';
import { GOTHIC, MONO, PALETTE, hex } from '../render/palette';
import type { Board } from '../platform';
import { apiBase, currentIdentity, ensureIdentity, formatCode, isInstalled } from '../platform/identity';
import { landingFromUrl } from '../share';
import { openCodePanel } from '../ui/codePanel';

export const VIEW_W = 540;
export const VIEW_H = 960;

/**
 * 記録一覧に使える縦の範囲。下端は引き継ぎコードの行の上。
 * **ここを踏み越えさせない。** 越えると下の説明文と重なる。
 */
const RECORDS_TOP = 552;
const RECORDS_BOTTOM = 728;
/** 1 本の表に見せる最大件数。入らなければ実測で減らす。 */
const RECORDS_MAX_ROWS = 3;

/** 走り方。デイリーだけは全員同じ海、キッズだけはランキング対象外。 */
export type RunMode = 'normal' | 'daily' | 'kids';

function modeLabel(mode: RunMode): string {
  return { normal: t().modeNormal, daily: t().modeDaily, kids: t().modeKids }[mode];
}

function boardLabel(board: Board): string {
  return { fastest: t().boardFastest, treasure: t().boardTreasure, overall: t().boardOverall }[
    board
  ];
}

/**
 * ステージ選択と記録。
 *
 * 走行そのものは `GameScene` に閉じているので、こちらは
 * 「どの海を、どの走り方で走るか」を決めるだけにしてある。
 */
export class MenuScene extends Phaser.Scene {
  private records = new Records();
  private mode: RunMode = 'normal';
  private selected = 0;

  private g!: Phaser.GameObjects.Graphics;
  private stageTexts: Phaser.GameObjects.Text[][] = [];
  private modeTexts: Phaser.GameObjects.Text[] = [];
  /** 3 本の表。横に 1 列ずつ。 */
  private recordTexts: Phaser.GameObjects.Text[] = [];
  private startText!: Phaser.GameObjects.Text;
  private noteText!: Phaser.GameObjects.Text;
  private codeText!: Phaser.GameObjects.Text;
  private langText!: Phaser.GameObjects.Text;
  private installText!: Phaser.GameObjects.Text;

  constructor() {
    super('menu');
  }

  create(data?: { stageId?: string; mode?: RunMode }): void {
    // **Phaser はシーンのインスタンスを使い回す。** 宣言時の初期化は 1 回きりなので、
    // 貯め込む配列は create() のたびに空にしないと、破棄済みの Text が残る。
    // 残ったまま refresh() が setColor すると canvas が null で落ち、
    // 「メニューへ」が効かないように見える。
    this.stageTexts = [];
    this.modeTexts = [];
    this.recordTexts = [];

    this.records = new Records();

    // 共有リンクから来た人は、その海に着地させる。
    //
    // **Phaser は data を渡さなくても `{}` を渡してくる。** 「呼び出し元が
    // 指定したか」は中身で見ないと分からず、存在で見ると URL が常に無視される。
    //
    // 効かせるのは URL に指定がある最初の 1 回だけ。ゲームから戻るときは
    // 呼び出し元が指定してくるので、そちらが勝つ。
    const explicit = data?.stageId || data?.mode ? data : null;
    const from = explicit ?? landingFromUrl() ?? undefined;
    if (from?.mode) this.mode = from.mode;
    if (from?.stageId) {
      const wanted = from.stageId.replace(/-kids$/, '');
      const index = STAGES.findIndex((s) => s.id === wanted);
      if (index >= 0) this.selected = index;
    }

    this.cameras.main.setBackgroundColor(PALETTE.deep);
    this.g = this.add.graphics().setDepth(0);

    this.add
      .text(VIEW_W / 2, 62, 'Escape the Waves', {
        fontFamily: MONO,
        fontSize: '20px',
        color: hex(PALETTE.swell),
      })
      .setOrigin(0.5, 0);
    this.add
      .text(VIEW_W / 2, 96, t().tagline, {
        fontFamily: GOTHIC,
        fontSize: '17px',
        color: hex(PALETTE.inkDim),
      })
      .setOrigin(0.5, 0);

    // ── 走り方 ──
    const modes: RunMode[] = ['normal', 'daily', 'kids'];
    modes.forEach((mode, i) => {
      const x = 60 + i * 150;
      const t = this.add
        .text(x + 65, 158, modeLabel(mode), {
          fontFamily: GOTHIC,
          fontSize: '16px',
          color: hex(PALETTE.inkDim),
        })
        .setOrigin(0.5)
        .setPadding(10, 12, 10, 12)
        .setInteractive({ useHandCursor: true });
      t.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
        e.stopPropagation();
        this.mode = mode;
        this.refresh();
      });
      this.modeTexts.push(t);
    });

    // ── ステージ ──
    STAGES.forEach((_stage, i) => {
      const y = 220 + i * 104;
      const name = this.add.text(48, y + 16, '', {
        fontFamily: GOTHIC,
        fontSize: '22px',
        color: hex(PALETTE.ink),
      });
      const character = this.add.text(48, y + 48, '', {
        fontFamily: GOTHIC,
        fontSize: '13px',
        color: hex(PALETTE.inkDim),
      });
      const best = this.add
        .text(VIEW_W - 48, y + 20, '', {
          fontFamily: MONO,
          fontSize: '13px',
          color: hex(PALETTE.sand),
          align: 'right',
          lineSpacing: 4,
        })
        .setOrigin(1, 0);

      const hit = this.add
        .zone(0, y, VIEW_W, 94)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
        e.stopPropagation();
        // 選んでいる海をもう一度押したら出発。1 タップ目は記録を見せる。
        if (this.selected === i) this.launch();
        else {
          this.selected = i;
          this.refresh();
        }
      });

      this.stageTexts.push([name, character, best]);
    });

    // ── 記録 ── 3 本を横に並べる。高さが走行回数で伸びない。
    this.recordTexts = [0, 1, 2].map((i) =>
      this.add.text(48 + i * 150, RECORDS_TOP, '', {
        fontFamily: MONO,
        fontSize: '13px',
        color: hex(PALETTE.inkDim),
        lineSpacing: 7,
      }),
    );

    // 言語の切り替え。**最初に選ばせない。** 端末の設定を既定にして、
    // **あそびかた。** 乗る波と潜る波の見分けが付かないと遊べないので、
    // メニューから一度読みに来られる場所を用意する。左上、言語と対で置く。
    const howTo = this.add
      .text(24, 60, t().howTo, {
        fontFamily: GOTHIC,
        fontSize: '13px',
        color: hex(PALETTE.inkDim),
      })
      .setOrigin(0, 0)
      .setPadding(12, 10, 12, 10)
      .setInteractive({ useHandCursor: true });
    howTo.on(
      'pointerdown',
      (_p: Phaser.Input.Pointer, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
        e.stopPropagation();
        this.scene.start('howto', {
          back: 'menu',
          stageId: STAGES[this.selected].id,
          mode: this.mode,
        });
      },
    );

    // 違うと思った人だけがここで直せればよい。右上に小さく置く。
    this.langText = this.add
      .text(VIEW_W - 24, 60, '', {
        fontFamily: MONO,
        fontSize: '13px',
        color: hex(PALETTE.inkDim),
      })
      .setOrigin(1, 0)
      .setPadding(12, 10, 12, 10)
      .setInteractive({ useHandCursor: true });
    this.langText.on(
      'pointerdown',
      (_p: Phaser.Input.Pointer, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
        e.stopPropagation();
        const next: Lang = lang() === 'ja' ? 'en' : 'ja';
        setLang(next);
        // 文言はシーン全体に散っているので、作り直す方が確実。
        this.scene.restart({ stageId: STAGES[this.selected].id, mode: this.mode });
      },
    );

    // ── 引き継ぎコード ──
    //
    // **iOS Safari は 7 日で localStorage を消す。** 端末生成の ID しか
    // 持たないと、1 週間空けた人は自己ベストも順位も失う。
    // サーバーが無いときは出しても押せないので、そもそも出さない。
    this.codeText = this.add
      .text(VIEW_W / 2, 744, '', {
        fontFamily: GOTHIC,
        fontSize: '13px',
        color: hex(PALETTE.inkDim),
      })
      .setOrigin(0.5, 0)
      .setPadding(16, 10, 16, 10)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });
    this.codeText.on(
      'pointerdown',
      (_p: Phaser.Input.Pointer, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
        e.stopPropagation();
        openCodePanel({
          code: currentIdentity().code,
          // 取り込みは別のインスタンスが localStorage へ書くので、
          // 表示している側は読み直さないと古いまま出る。
          onRestored: () => {
            this.records.reload();
            this.refresh();
          },
        });
      },
    );

    // ホーム画面に置いてもらうこと自体が記録の生存策。
    // 置いてある人には言わない。できたことは二度と教えない。
    this.installText = this.add
      .text(VIEW_W / 2, 776, '', {
        fontFamily: GOTHIC,
        fontSize: '12px',
        color: hex(PALETTE.inkFaint),
        align: 'center',
      })
      .setOrigin(0.5, 0)
      .setVisible(false);

    this.noteText = this.add
      .text(VIEW_W / 2, 806, '', {
        fontFamily: GOTHIC,
        fontSize: '13px',
        color: hex(PALETTE.inkFaint),
        align: 'center',
        lineSpacing: 5,
      })
      .setOrigin(0.5, 0);

    this.startText = this.add
      .text(VIEW_W / 2, 878, '', {
        fontFamily: GOTHIC,
        fontSize: '20px',
        color: hex(PALETTE.swell),
      })
      .setOrigin(0.5, 0)
      .setPadding(24, 16, 24, 16)
      .setInteractive({ useHandCursor: true });
    this.startText.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
      e.stopPropagation();
      this.launch();
    });

    this.input.keyboard?.on('keydown-SPACE', () => this.launch());
    this.input.keyboard?.on('keydown-DOWN', () => {
      this.selected = (this.selected + 1) % STAGES.length;
      this.refresh();
    });
    this.input.keyboard?.on('keydown-UP', () => {
      this.selected = (this.selected + STAGES.length - 1) % STAGES.length;
      this.refresh();
    });

    this.refresh();

    // サーバーがあれば、この端末の身元を 1 回だけ発行してもらう。
    // 失敗しても何も起きない（端末内 ID のまま遊べる）。
    void ensureIdentity().then(() => this.scene.isActive() && this.refresh());
  }

  private keyFor(stage: StageConfig): string {
    const id = this.mode === 'kids' ? `${stage.id}-kids` : stage.id;
    return boardKey(id, this.mode === 'daily' ? todayKey() : undefined);
  }

  private refresh(): void {
    this.g.clear();

    // 押すと切り替わる先を出す。いまの言語を出しても押す理由にならない。
    this.langText.setText(lang() === 'ja' ? 'EN' : '日本語');

    const code = currentIdentity().code;
    this.codeText
      .setVisible(apiBase() !== null)
      .setText(code ? t().transferCode(formatCode(code)) : t().enterCode);
    this.installText
      .setVisible(!isInstalled())
      .setText(t().installHint);

    this.modeTexts.forEach((t, i) => {
      const active = (['normal', 'daily', 'kids'] as RunMode[])[i] === this.mode;
      t.setColor(hex(active ? PALETTE.deep : PALETTE.inkDim));
      const b = t.getBounds();
      this.g.fillStyle(active ? PALETTE.swell : PALETTE.mid, active ? 1 : 0.5);
      this.g.fillRoundedRect(b.x, b.y, b.width, b.height, 6);
    });

    STAGES.forEach((stage, i) => {
      const y = 220 + i * 104;
      const active = i === this.selected;
      this.g.fillStyle(active ? PALETTE.mid : PALETTE.deep, active ? 1 : 0.6);
      this.g.fillRoundedRect(28, y, VIEW_W - 56, 94, 10);
      this.g.lineStyle(active ? 2 : 1, active ? PALETTE.swell : PALETTE.inkFaint, active ? 0.9 : 0.3);
      this.g.strokeRoundedRect(28, y, VIEW_W - 56, 94, 10);

      const [name, character, best] = this.stageTexts[i];
      name.setText(stageName(stage.id, stage.name));
      name.setColor(hex(active ? PALETTE.ink : PALETTE.inkDim));
      character.setText(this.characterOf(stage));

      const key = this.keyFor(stage);
      const fastest = this.records.best(key, 'fastest');
      const overall = this.records.best(key, 'overall');
      best.setText(
        fastest && overall
          ? t().bestLine(formatTime(fastest.timeSeconds), overall.total)
          : t().noRecord,
      );
      best.setColor(hex(fastest ? PALETTE.sand : PALETTE.inkFaint));
    });

    // 選んでいる海の記録。3 本立てをそのまま見せる。
    //
    // **入る件数は実測で決める。** 決め打つと、字の大きさや言語を変えた
    // 誰かが後から静かに踏み越える。ここは一度それで重なった場所。
    const stage = STAGES[this.selected];
    const key = this.keyFor(stage);
    const boards: Board[] = ['fastest', 'treasure', 'overall'];
    const value = (board: Board, r: RunRecord): string =>
      board === 'fastest'
        ? formatTime(r.timeSeconds)
        : board === 'treasure'
          ? t().points(r.treasurePoints)
          : t().points(r.total);

    for (let rows = RECORDS_MAX_ROWS; rows >= 1; rows--) {
      let tallest = 0;
      this.recordTexts.forEach((text, i) => {
        const board = boards[i];
        const top = this.records.top(key, board, rows);
        const lines = [boardLabel(board)];
        if (top.length === 0) lines.push(' —');
        top.forEach((r, n) => lines.push(` ${n + 1}. ${value(board, r)}`));
        text.setText(lines.join('\n'));
        tallest = Math.max(tallest, text.height);
      });
      if (tallest <= RECORDS_BOTTOM - RECORDS_TOP) break;
    }

    this.noteText.setText(
      this.mode === 'daily'
        ? t().noteDaily(todayKey())
        : this.mode === 'kids'
          ? t().noteKids
          : t().noteNormal(this.records.runCount(key)),
    );
    this.startText.setText(t().depart(stageName(stage.id, stage.name)));
  }

  private characterOf(stage: StageConfig): string {
    const parts = [t().offshore(stage.length)];
    if (stage.tide) parts.push(t().lowTide);
    if (stage.ampRange[1] >= 2.4) parts.push(t().bigWaves);
    if (stage.ripCount >= 2) parts.push(t().strongRip);
    return parts.join(t().traitSeparator);
  }

  private launch(): void {
    this.scene.start('game', {
      stageId: STAGES[this.selected].id,
      mode: this.mode,
    });
  }
}
